import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

const SECTIONS_COLLECTION = 'website_sections';
const SETTINGS_COLLECTION = 'cms_settings';

const cmsCache = new Map();
const CACHE_TTL_MS = 2 * 60 * 1000;

export const clearCmsCache = () => {
  cmsCache.clear();
};

/**
 * Get section by sectionKey
 */
export const getSectionByKey = async (key) => {
  try {
    const cached = cmsCache.get(key);
    if (cached && (Date.now() - cached.timestamp < CACHE_TTL_MS)) {
      return cached.data;
    }

    // 1. Direct ID lookup first (used by saveWebsiteSection)
    const docRef = doc(db, SECTIONS_COLLECTION, key);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = {
        _id: docSnap.id,
        id: docSnap.id,
        ...docSnap.data()
      };
      cmsCache.set(key, { timestamp: Date.now(), data });
      return data;
    }

    // 2. Query fallback if saved under auto ID
    const sectionsRef = collection(db, SECTIONS_COLLECTION);
    const q = query(sectionsRef, where('sectionKey', '==', key));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      cmsCache.set(key, { timestamp: Date.now(), data: null });
      return null;
    }
    const docSnap2 = snapshot.docs[0];
    const data2 = {
      _id: docSnap2.id,
      id: docSnap2.id,
      ...docSnap2.data()
    };
    cmsCache.set(key, { timestamp: Date.now(), data: data2 });
    return data2;
  } catch (error) {
    console.error('Error fetching CMS section by key from Firestore:', error);
    throw error;
  }
};

/**
 * Upsert website section by sectionKey
 */
export const saveWebsiteSection = async (key, sectionData) => {
  try {
    clearCmsCache();
    const docRef = doc(db, SECTIONS_COLLECTION, key);
    await setDoc(docRef, {
      ...sectionData,
      sectionKey: key,
      updatedAt: serverTimestamp()
    }, { merge: true });

    const updatedDoc = await getDoc(docRef);
    return {
      _id: key,
      id: key,
      ...updatedDoc.data()
    };
  } catch (error) {
    console.error('Error saving CMS section to Firestore:', error);
    throw error;
  }
};

/**
 * Get all website sections
 */
export const getAllSections = async () => {
  try {
    const sectionsRef = collection(db, SECTIONS_COLLECTION);
    const snapshot = await getDocs(sectionsRef);
    const sections = snapshot.docs.map(docSnap => ({
      _id: docSnap.id,
      id: docSnap.id,
      ...docSnap.data()
    }));
    return { sections };
  } catch (error) {
    console.error('Error fetching all CMS sections from Firestore:', error);
    throw error;
  }
};

/**
 * Get site settings
 */
export const getCmsSettings = async () => {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, 'global_settings');
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return {};
    }
    return docSnap.data();
  } catch (error) {
    console.error('Error fetching CMS settings from Firestore:', error);
    return {};
  }
};

/**
 * Save site settings
 */
export const updateCmsSettings = async (settingsData) => {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, 'global_settings');
    await setDoc(docRef, {
      ...settingsData,
      updatedAt: serverTimestamp()
    }, { merge: true });
    return { message: 'Settings saved successfully', settings: settingsData };
  } catch (error) {
    console.error('Error updating CMS settings in Firestore:', error);
    throw error;
  }
};
